import { ChartBar, LayoutDashboard as LD, LogOut, Shield, Swords, Triangle, Trophy, User } from "lucide-solid";
import { For, Show, createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Avatar } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { useAuth } from "~/lib/auth";
import { FEATURES, type FeatureKey } from "~/lib/features";

import { Link, useRouterState } from "@tanstack/solid-router";
import { headerLogoUrl, kanjiUrl, selectBgSlim, selectBgThick } from "~/lib/svg-assets";
import { haptic } from "~/lib/haptics";
import { cn } from "~/lib/utils";

// ── Brush stroke SVG pools ───────────────────────────────────────────────────
const SLIM = selectBgSlim;
const THICK = selectBgThick;
const usageCountsSlim = new Map(SLIM.map(item => [item, 0]));
const usageCountsThick = new Map(THICK.map(item => [item, 0]));

function luRand(pool: string[], counts: Map<string, number>): string {
    const minUsage = Math.min(...counts.values());
    const candidates = pool.filter(item => counts.get(item) === minUsage);
    const selected = candidates[Math.floor(Math.random() * candidates.length)];
    counts.set(selected, counts.get(selected)! + 1);
    return selected;
}

const rotationRange = 8; // 8 degrees
const randRot = () => Math.random() > 0.5 ? Math.round(Math.random() * (rotationRange * 2) - rotationRange) : (Math.round(Math.random() * (rotationRange * 2) - rotationRange) - 180);

// Assigned once per page-load so brush strokes don't re-randomize on re-render
const DESKTOP_NAV: { to: string; label: string; icon: any; feature: FeatureKey; bg: string; rot: number }[] = [
  { to: "/dashboard",   label: "Dashboard",   icon: LD,       feature: "dashboard",   bg: luRand(SLIM, usageCountsSlim), rot: randRot() },
  { to: "/rankings",    label: "Rankings",    icon: ChartBar, feature: "rankings",    bg: luRand(SLIM, usageCountsSlim), rot: randRot() },
  { to: "/duels",       label: "Duels",       icon: Swords,   feature: "duels",       bg: luRand(SLIM, usageCountsSlim), rot: randRot() },
  { to: "/tournaments", label: "Tournaments", icon: Trophy,   feature: "tournaments", bg: luRand(SLIM, usageCountsSlim), rot: randRot() },
];
const ADMIN_BG = { bg: luRand(SLIM, usageCountsSlim), rot: randRot() };

const BOTTOM_MAIN: { to: string; label: string; icon: any; feature: FeatureKey; bg: string; rot: number }[] = [
  { to: "/dashboard",   label: "Dash",  icon: LD,       feature: "dashboard",   bg: luRand(THICK, usageCountsThick), rot: randRot() },
  { to: "/rankings",    label: "Ranks", icon: ChartBar, feature: "rankings",    bg: luRand(THICK, usageCountsThick), rot: randRot() },
  { to: "/tournaments", label: "Tourn", icon: Trophy,   feature: "tournaments", bg: luRand(THICK, usageCountsThick), rot: randRot() },
];
const DUELS_BG = { bg: luRand(THICK, usageCountsThick), rot: randRot() };
const MORE_BG  = { bg: luRand(THICK, usageCountsThick), rot: randRot() };

// ── Radial grid positions (px from trigger center, x<0 = left, y<0 = up) ───
const RADIAL_GRID: { x: number; y: number }[][] = [
  [{ x: -28, y: -95 }],
  [{ x: 0, y: -90 }, { x: -80, y: -90 }],
  [{ x: 0, y: -90 }, { x: -80, y: -90 }, { x: -40, y: -165 }],
  [{ x: 0, y: -90 }, { x: -80, y: -90 }, { x: 0, y: -165 }, { x: -80, y: -165 }],
];

// ── BrushBg: brand-colored SVG mask, reveals left→right ─────────────────────
function BrushBg(props: { src: string; rot?: number }) {
  return (
    <span
      class="brush-bg"
      style={{
        "-webkit-mask-image": `url('${props.src}')`,
        "mask-image": `url('${props.src}')`,
        "transform": `rotate(${props.rot ?? 0}deg)`,
      }}
      aria-hidden="true"
    />
  );
}

// ── Shared leaving-animation primitive ──────────────────────────────────────
// Detects when a tab stops being active and briefly adds the `leaving` class
// so brush-erase animation plays, then cleans up.
function useLeavingClass(to: string): () => boolean {
  const isActive = useRouterState({ select: (s) => s.location.pathname === to });
  const [leaving, setLeaving] = createSignal(false);
  let timer: ReturnType<typeof setTimeout> | undefined;

  onCleanup(() => clearTimeout(timer));

  createEffect((prev: boolean) => {
    const current = isActive();
    if (prev && !current) {
      setLeaving(true);
      clearTimeout(timer);
      timer = setTimeout(() => setLeaving(false), 350);
    }
    return current;
  }, isActive());

  return leaving;
}

// ── Desktop nav tab ──────────────────────────────────────────────────────────
function NavTab(props: { to: string; label: string; icon: any; bg: string; rot: number }) {
  const leaving = useLeavingClass(props.to);
  return (
    <Link
      to={props.to}
      class="nav-tab relative flex items-center gap-1.5 px-3 py-2 text-sm font-bold uppercase tracking-wide text-text-muted hover:text-text-base transition-colors"
      activeProps={{ class: "active" }}
      classList={{ leaving: leaving() }}
      onClick={() => haptic.tap()}
    >
      <BrushBg src={props.bg} rot={props.rot} />
      <props.icon class="relative z-1 hidden @nav:block" size={15} />
      <span class="relative z-1 hidden @nav:block">{props.label}</span>
      <props.icon class="relative z-1 block @nav:hidden" size={20} />
      <span class="relative z-1 block @nav:hidden">&nbsp;</span>
    </Link>
  );
}

// ── Mobile bottom tab ────────────────────────────────────────────────────────
function BottomTab(props: { to: string; label: string; icon: any; bg: string; rot: number }) {
  const leaving = useLeavingClass(props.to);
  return (
    <Link
      to={props.to}
      class="bottom-tab flex-1 relative flex flex-col items-center justify-center gap-0.5 py-2 text-text-faint transition-colors overflow-hidden"
      activeProps={{ class: "active" }}
      classList={{ leaving: leaving() }}
      onClick={() => haptic.tap()}
    >
      <BrushBg src={props.bg} rot={props.rot} />
      <props.icon class="relative z-[1]" size={20} />
      <span class="relative z-[1] text-[9px] font-bold uppercase tracking-wide">{props.label}</span>
    </Link>
  );
}

export function Navbar() {
  const { state, signIn, signOut } = useAuth();
  const [radialOpen, setRadialOpen] = createSignal(false);

  // Hard reload on logout - see todo_logout_spa_redirect.md
  const handleSignOut = async () => {
    await signOut();
    window.location.assign("/");
  };

  // When user has no extra nav items beyond Duels (no reports, not admin/judge),
  // show Duels directly as the 4th slot and expose profile link in the top bar.
  const showDirectDuels = createMemo(() =>
    FEATURES.duels && (
      !FEATURES.admin ||
      (state.user?.role !== "admin" && state.user?.role !== "judge")
    )
  );

  const radialItems = createMemo(() => {
    const items: { to: string; label: string; icon: any }[] = [];
    if (FEATURES.duels)
      items.push({ to: "/duels", label: "Duels", icon: Swords });
    if (FEATURES.profile && state.user)
      items.push({ to: `/profile/${state.user.username}`, label: "Profile", icon: User });
    if (FEATURES.admin && (state.user?.role === "admin" || state.user?.role === "judge"))
      items.push({ to: "/admin", label: "Admin", icon: Shield });
    return items;
  });

  const grid = createMemo(() => RADIAL_GRID[Math.min(radialItems().length - 1, 3)] ?? []);

  const activeRadialItem = useRouterState({
    select: (s) => radialItems().find(item => s.location.pathname.startsWith(item.to)) ?? null,
  });
  const isAnyRadialActive = () => activeRadialItem() !== null;

  return (
    <>
      {/* ── Top bar ────────────────────────────────────────────────────── */}
      {/* padding-top fills the iOS safe area (camera bar / notch) with the bar's
          blurred background so the logo + tabs sit below it, not under it. */}
      <nav class="sticky top-0 z-40 border-b border-border bg-surface-1/20 backdrop-blur-md @container"
        style="padding-top: env(safe-area-inset-top, 0px);">
        <div class="mx-auto max-w-7xl px-4 sm:px-6">
          <div class="flex h-14 items-center justify-between">
            <Link to="/" class="relative flex items-center gap-2.5 w-70 h-full overflow-hidden mr-4 @container">
              <img src={headerLogoUrl} alt="Kurotsumi CRG" class="hidden @logo:block absolute w-full object-contain z-0" />
              <img src={kanjiUrl} alt="Kurotsumi CRG" class="block @logo:hidden absolute h-12 object-contain z-0" />
            </Link>

            {/* Desktop nav tabs — hidden on mobile */}
            <Show when={state.user}>
              <div class="hidden md:flex items-center flex-1">
                <For each={DESKTOP_NAV.filter((l) => FEATURES[l.feature])}>
                  {(link) => <NavTab to={link.to} label={link.label} icon={link.icon} bg={link.bg} rot={link.rot} />}
                </For>
                <Show when={FEATURES.admin && (state.user?.role === "admin" || state.user?.role === "judge")}>
                  <NavTab to="/admin" label="Admin" icon={Shield} bg={ADMIN_BG.bg} rot={ADMIN_BG.rot} />
                </Show>
              </div>
            </Show>

            {/* Avatar + logout */}
            <div class="flex items-center gap-2">
              <Show
                when={state.user}
                fallback={
                  <Button size="sm" class="whitespace-nowrap" onClick={signIn} disabled={state.loading}>
                    Login with Discord
                  </Button>
                }
              >
                <Show
                  when={FEATURES.profile}
                  fallback={
                    <div class="flex items-center gap-2 px-2 py-1">
                      <Avatar src={state.user?.avatarUrl ?? undefined} fallback={state.user?.displayName} size="sm" />
                    </div>
                  }
                >
                  {/* Desktop: always links to profile */}
                  <Link
                    to="/profile/$username"
                    search={{ tab: "progression" }}
                    params={{ username: state.user!.username }}
                    class="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-surface-3 transition-colors"
                  >
                    <Avatar src={state.user?.avatarUrl ?? undefined} fallback={state.user?.displayName} size="sm" />
                    <span class="hidden md:inline text-sm font-semibold text-text-base">{state.user?.displayName}</span>
                  </Link>
                </Show>
                <Button variant="ghost" size="icon" onClick={handleSignOut} title="Sign out">
                  <LogOut size={16} />
                </Button>
              </Show>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Mobile bottom nav ──────────────────────────────────────────── */}
      <Show when={state.user}>
        <div class="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-surface-1/95 backdrop-blur-md"
        // Add extra padding to account for mobile home indicator
        style="height: calc(64px + env(safe-area-inset-bottom, 0px));padding-bottom: env(safe-area-inset-bottom, 0px);">
          <div class="flex items-stretch h-16">
            <For each={BOTTOM_MAIN.filter((l) => FEATURES[l.feature])}>
              {(link) => <BottomTab to={link.to} label={link.label} icon={link.icon} bg={link.bg} rot={link.rot} />}
            </For>

            {/* 4th slot: direct Duels link for regular users, radial for everyone else */}
            <Show
              when={showDirectDuels()}
              fallback={
                <Show when={radialItems().length > 0}>
                  <div class="flex-1 relative flex items-center justify-center">
                    <Show when={radialOpen()}>
                      <div class="fixed inset-0 z-40" onClick={() => setRadialOpen(false)} />
                    </Show>
                    <Show when={radialOpen()}>
                      <For each={radialItems()}>
                        {(item, i) => (
                          <Link
                            to={item.to}
                            class="radial-item absolute z-50 w-14 h-14 flex flex-col items-center justify-center gap-0.5 rounded-xl bg-surface-3 border border-border text-text-muted hover:text-text-base hover:border-brand/30 hover:bg-surface-4 transition-colors text-[9px] font-bold uppercase tracking-wide"
                            style={{
                              bottom: "50%",
                              left: "50%",
                              transform: `translate(calc(-50% + ${grid()[i()].x}px), calc(50% + ${grid()[i()].y}px))`,
                              "animation-delay": `${i() * 40}ms`,
                            }}
                            activeProps={{ class: "text-brand! border-brand/40 bg-brand/10" }}
                            onClick={() => { haptic.tap(); setRadialOpen(false); }}
                          >
                            <item.icon size={20} />
                            {item.label}
                          </Link>
                        )}
                      </For>
                    </Show>
                    <button
                      class={`bottom-tab relative w-full h-full flex flex-col items-center justify-center gap-0.5 py-2 transition-colors overflow-hidden ${isAnyRadialActive() ? "active" : radialOpen() ? "text-brand" : "text-text-faint"}`}
                      onClick={() => { haptic.tap(); setRadialOpen((v) => !v); }}
                      aria-label="More navigation"
                    >
                      <BrushBg src={MORE_BG.bg} rot={MORE_BG.rot} />
                      <div class="relative z-1">
                        <Show when={activeRadialItem()}>
                          <Triangle size={12} class={cn("absolute -top-1 -right-3.5 z-1 stroke-3 transform transition-transform", radialOpen() ? "-rotate-180" : "")} />
                        </Show>
                        <Dynamic component={activeRadialItem()?.icon ?? Triangle} size={20} class={cn("relative z-1 transform transition-transform", radialOpen() && !activeRadialItem() ? "-rotate-180" : "")} />
                      </div>
                      <span class="relative z-1 text-[9px] font-bold uppercase tracking-wide">{activeRadialItem()?.label ?? "More"}</span>
                    </button>
                  </div>
                </Show>
              }
            >
              <BottomTab to="/duels" label="Duels" icon={Swords} bg={DUELS_BG.bg} rot={DUELS_BG.rot} />
            </Show>
          </div>
        </div>
      </Show>
    </>
  );
}
